import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Users as UsersIcon, Search, Grid, List, CreditCard } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDateLocal } from '@/utils/helpers';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubscriptionsModalOpen, setIsSubscriptionsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSubscriptions, setClientSubscriptions] = useState([]);
  const [subscriptionsTab, setSubscriptionsTab] = useState('all');
  const [editingClient, setEditingClient] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = clients.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.phone.includes(searchQuery) ||
        (c.email && c.email.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchQuery, clients]);

  // Calculate pagination
  const totalPages = itemsPerPage === 'all' 
    ? 1 
    : Math.ceil(filteredClients.length / itemsPerPage);
  
  const paginatedClients = itemsPerPage === 'all' 
    ? filteredClients 
    : filteredClients.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === 'all' ? 'all' : parseInt(value));
    setCurrentPage(1);
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get('/clients');
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate for duplicates first
      const validationParams = new URLSearchParams({
        name: formData.name,
        ...(formData.phone && { phone: formData.phone }),
        ...(formData.email && { email: formData.email }),
        ...(editingClient && { client_id: editingClient.id })
      });
      
      const validationResponse = await axios.post(`/clients/validate-duplicate?${validationParams}`);
      
      if (validationResponse.data.is_duplicate) {
        const matchingFields = validationResponse.data.matching_fields.join(', ');
        const existingClient = validationResponse.data.existing_client;
        
        toast.error(
          `Ya existe un cliente con el mismo ${matchingFields}: ${existingClient.name}`,
          { duration: 5000 }
        );
        setLoading(false);
        return;
      }

      // If no duplicates, proceed with save
      if (editingClient) {
        await axios.put(`/clients/${editingClient.id}`, formData);
        toast.success('Cliente actualizado');
      } else {
        await axios.post('/clients', formData);
        toast.success('Cliente agregado');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      phone: client.phone,
      email: client.email || '',
      address: client.address || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;

    try {
      await axios.delete(`/clients/${clientId}`);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', email: '', address: '' });
    setEditingClient(null);
  };

  const handleViewSubscriptions = async (client) => {
    setSelectedClient(client);
    setIsSubscriptionsModalOpen(true);
    try {
      const response = await axios.get(`/subscriptions/client/${client.id}`);
      setClientSubscriptions(response.data);
    } catch (error) {
      toast.error('Error al cargar suscripciones');
    }
  };

  const getFilteredSubscriptions = () => {
    if (subscriptionsTab === 'all') {
      return clientSubscriptions;
    }
    return clientSubscriptions.filter(sub => sub.status === subscriptionsTab);
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activa';
      case 'expiring_soon': return 'Por Vencer';
      case 'expired': return 'Vencida';
      default: return status;
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'daily': return 'Diario';
      case '15days': return '15 Días';
      case 'monthly': return 'Mensual';
      default: return type;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'expiring_soon': return 'status-expiring';
      case 'expired': return 'status-expired';
      default: return '';
    }
  };

  return (
    <div className="clients-page" data-testid="clients-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">Gestiona la base de datos de clientes</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} data-testid="add-client-btn">
          <Plus size={20} />
          Agregar Cliente
        </Button>
      </div>

      <Card className="search-card">
        <div className="search-controls">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <Input
              type="text"
              placeholder="Buscar por nombre, teléfono o email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              data-testid="search-clients-input"
            />
          </div>
          <div className="view-toggle">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-btn"
            >
              <Grid size={18} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              data-testid="list-view-btn"
            >
              <List size={18} />
            </Button>
          </div>
        </div>
      </Card>

      {viewMode === 'grid' ? (
        <div className="clients-grid">
        {paginatedClients.map((client) => (
          <Card key={client.id} className="client-card" data-testid={`client-card-${client.id}`}>
            <div className="client-avatar">
              {client.name.charAt(0).toUpperCase()}
            </div>
            <div className="client-info">
              <h3 className="client-name">{client.name}</h3>
              <p className="client-phone">{client.phone}</p>
              {client.email && <p className="client-email">{client.email}</p>}
              {client.address && <p className="client-address">{client.address}</p>}
            </div>
            <div className="client-actions">
              <Button size="sm" variant="default" onClick={() => handleViewSubscriptions(client)} data-testid={`view-subs-${client.id}`}>
                <CreditCard size={14} />
                Suscripciones
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleEdit(client)} data-testid={`edit-client-${client.id}`}>
                <Edit size={16} />
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(client.id)} data-testid={`delete-client-${client.id}`}>
                <Trash2 size={16} />
              </Button>
            </div>
          </Card>
        ))}
        </div>
      ) : (
        <Card className="table-card">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Dirección</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClients.map((client) => (
                  <tr key={client.id} data-testid={`client-row-${client.id}`}>
                    <td><strong>{client.name}</strong></td>
                    <td>{client.phone}</td>
                    <td>{client.email || '-'}</td>
                    <td>{client.address || '-'}</td>
                    <td>
                      <div className="table-actions">
                        <Button size="sm" variant="default" onClick={() => handleViewSubscriptions(client)}>
                          <CreditCard size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(client)}>
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(client.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination Controls */}
      {filteredClients.length > 0 && (
        <Card className="pagination-card">
          <div className="pagination-controls">
            <div className="items-per-page">
              <Label htmlFor="items-per-page">Elementos por página:</Label>
              <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="items-per-page-select" data-testid="items-per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {itemsPerPage !== 'all' && (
              <div className="pagination-info">
                <span>
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredClients.length)} de {filteredClients.length}
                </span>
              </div>
            )}

            {itemsPerPage !== 'all' && totalPages > 1 && (
              <div className="pagination-buttons">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  data-testid="prev-page-btn"
                >
                  Anterior
                </Button>

                <div className="page-numbers">
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          data-testid={`page-${page}-btn`}
                        >
                          {page}
                        </Button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return <span key={page} className="page-ellipsis">...</span>;
                    }
                    return null;
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  data-testid="next-page-btn"
                >
                  Siguiente
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {filteredClients.length === 0 && (
        <div className="empty-state">
          <UsersIcon size={64} />
          <h3>No hay clientes registrados</h3>
          <p>Agrega tu primer cliente para comenzar</p>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="client-dialog">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="client-form">
            <div className="form-group">
              <Label htmlFor="name">Nombre*</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="client-name-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="phone">Teléfono*</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
                data-testid="client-phone-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="client-email-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                data-testid="client-address-input"
              />
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-client-btn">
                {loading ? 'Guardando...' : (editingClient ? 'Actualizar' : 'Guardar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isSubscriptionsModalOpen} onOpenChange={setIsSubscriptionsModalOpen}>
        <DialogContent className="subscriptions-modal">
          <DialogHeader>
            <DialogTitle>Suscripciones de {selectedClient?.name}</DialogTitle>
          </DialogHeader>
          
          <Tabs value={subscriptionsTab} onValueChange={setSubscriptionsTab} className="modal-tabs">
            <TabsList className="tabs-list-modal">
              <TabsTrigger value="all">Todas</TabsTrigger>
              <TabsTrigger value="active">Activas</TabsTrigger>
              <TabsTrigger value="expiring_soon">Por Vencer</TabsTrigger>
              <TabsTrigger value="expired">Vencidas</TabsTrigger>
            </TabsList>

            <TabsContent value={subscriptionsTab} className="modal-tab-content">
              {getFilteredSubscriptions().length === 0 ? (
                <div className="empty-subs">
                  <CreditCard size={48} />
                  <p>No hay suscripciones en esta categoría</p>
                </div>
              ) : (
                <div className="subs-list">
                  {getFilteredSubscriptions().map((sub) => (
                    <Card key={sub.id} className="sub-item">
                      <div className="sub-header">
                        <span className={`sub-status ${getStatusBadgeClass(sub.status)}`}>
                          {getStatusText(sub.status)}
                        </span>
                        <span className="sub-type">{getTypeText(sub.subscription_type)}</span>
                      </div>
                      <div className="sub-details">
                        <div className="sub-row">
                          <span className="sub-label">Precio:</span>
                          <span className="sub-value">${sub.price.toFixed(2)}</span>
                        </div>
                        <div className="sub-row">
                          <span className="sub-label">Inicio:</span>
                          <span className="sub-value">{formatDateLocal(sub.start_date)}</span>
                        </div>
                        <div className="sub-row">
                          <span className="sub-label">Vencimiento:</span>
                          <span className="sub-value">{formatDateLocal(sub.end_date)}</span>
                        </div>
                        <div className="sub-row">
                          <span className="sub-label">Creado por:</span>
                          <span className="sub-value">{sub.created_by}</span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .clients-page {
          max-width: 1400px;
        }

        .client-dialog {
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .clients-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .client-card {
          padding: 1.5rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.3s;
        }

        .client-card:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .client-avatar {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .client-info {
          margin-bottom: 1rem;
        }

        .client-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .client-phone,
        .client-email,
        .client-address {
          font-size: 0.9375rem;
          color: #64748b;
          margin-bottom: 0.25rem;
        }

        .client-actions {
          display: flex;
          gap: 0.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
          color: #64748b;
        }

        .client-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-top: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 1rem;
        }

        .search-card {
          padding: 1rem;
          margin-bottom: 2rem;
        }

        .search-controls {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: #64748b;
        }

        .search-input {
          padding-left: 3rem;
        }

        .view-toggle {
          display: flex;
          gap: 0.5rem;
        }

        .table-card {
          padding: 0;
          overflow: hidden;
        }

        .table-container {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table thead {
          background: #f8fafc;
        }

        .data-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.875rem;
          color: #64748b;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        .data-table td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.9375rem;
        }

        .data-table tbody tr {
          transition: background 0.2s;
        }

        .data-table tbody tr:hover {
          background: #f8fafc;
        }

        .table-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .subscriptions-modal {
          max-width: 700px;
          max-height: 85vh;
          overflow-y: auto;
        }

        .modal-tabs {
          margin-top: 1rem;
        }

        .tabs-list-modal {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: #f8fafc;
          padding: 0.25rem;
          border-radius: 8px;
        }

        .modal-tab-content {
          margin-top: 1.5rem;
          max-height: 400px;
          overflow-y: auto;
        }

        .empty-subs {
          text-align: center;
          padding: 3rem 2rem;
          color: #94a3b8;
        }

        .empty-subs p {
          margin-top: 1rem;
          font-size: 1rem;
          color: #64748b;
        }

        .subs-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .sub-item {
          padding: 1rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .sub-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .sub-status {
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-active {
          background: #d1fae5;
          color: #065f46;
        }

        .status-expiring {
          background: #fed7aa;
          color: #92400e;
        }

        .status-expired {
          background: #fee2e2;
          color: #991b1b;
        }

        .sub-type {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
        }

        .sub-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .sub-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9375rem;
        }

        .sub-label {
          color: #64748b;
        }

        .sub-value {
          font-weight: 600;
          color: #1e293b;
        }

        .pagination-card {
          margin-top: 2rem;
          padding: 1.5rem;
        }

        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .items-per-page {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .items-per-page-select {
          width: 120px;
        }

        .pagination-info {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .pagination-buttons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .page-numbers {
          display: flex;
          gap: 0.25rem;
        }

        .page-ellipsis {
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          color: #64748b;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
          }

          .clients-grid {
            grid-template-columns: 1fr;
          }

          .search-controls {
            flex-direction: column;
          }

          .view-toggle {
            width: 100%;
          }

          .view-toggle button {
            flex: 1;
          }

          .pagination-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .items-per-page {
            justify-content: space-between;
          }

          .pagination-buttons {
            flex-direction: column;
            width: 100%;
          }

          .page-numbers {
            justify-content: center;
            flex-wrap: wrap;
          }

          .pagination-info {
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Clients;
